import axios from "axios";
import getAccesstokenNameByRoute from './getAccesstokenNameByRoute';
import { refreshAccessToken, isRefreshableAuthError, isPublicAuthExemptEndpoint } from './tokenRefresh';
import commonLogout, { isAccountChangedElsewhere } from '../common-logout';
import sensitiveRequest from './sensitiveRequest';
import { getTenantKey } from '../tenant-context';

const {shouldLogRequestDiagnostics} = sensitiveRequest;

// Full config:  https://github.com/axios/axios#request-config
// axios.defaults.baseURL = process.env.baseURL || process.env.apiUrl || '';
// axios.defaults.headers.common['Authorization'] = AUTH_TOKEN;
// axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

const _custom_base_axios = axios.create({
    baseURL: process.env.VUE_APP_BACKENDHOST,
    headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        // "Pragma": "no-cache",
        "Expires": '0'
    },
    withCredentials: true
})

_custom_base_axios.interceptors.request.use(
	function (config) {
		// 다른 탭에서 계정이 바뀌었으면 이 탭(이전 계정 화면)의 쓰기 요청을 보내지 않는다 — App.vue 가 안내한다
		if (isAccountChangedElsewhere(config)) {
			if (window.EventBus) window.EventBus.$emit('account-changed-elsewhere');
			return Promise.reject(Object.assign(new Error('account changed in another tab'), { code: 'ACCOUNT_CHANGED' }));
		}
		const _accesstoken_name = getAccesstokenNameByRoute()
		let url = config.url
		if (url.includes(process.env.VUE_APP_S3_BUCKET_NAME) && url.includes("s3.amazonaws")){
			config.headers = {}
			config.baseURL = ""
		} else {
			config.headers['Authorization'] = `Bearer ${localStorage.getItem(_accesstoken_name)}`;
            config['withCredentials'] = true
            // 테넌트 힌트 헤더 — env(로컬 개발 override, #905) 우선, 없으면 hostname 판정 키.
            // 백엔드 계약(docs/multitenant-backend-contract.md §1): prod 는 Origin 우선이라 힌트일 뿐 인증 아님.
            const _subdomain = process.env.VUE_APP_SUBDOMAIN || getTenantKey();
            if (_subdomain) {
                config.headers['X-Subdomain'] = _subdomain;
            }
		}

		if (shouldLogRequestDiagnostics(process.env.VUE_APP_BUILD_ENV, config)) {
			console.log(` ~~~~~~~~~~~~ - axios interceptors - (request) - (test & develop log) ~~~ `);
			console.log(` 		~~~~~~~~~~~~ - request - (config) ~~~ `);
			console.log(` 		~ config => `, config);

			console.log(` 		~ localStorage.getItem(${_accesstoken_name}) => `, localStorage.getItem(_accesstoken_name));
			console.log(` ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ `);
		}
        
		return config;
	},
	async function (error) {
		// Do something with request error
		const _accesstoken_name = getAccesstokenNameByRoute()

        if (shouldLogRequestDiagnostics(process.env.VUE_APP_BUILD_ENV, error?.config)) {
            console.log(` ~~~~~~~~~~~~ - axios interceptors - (request) - (test & develop log) ~~~ `);
            console.log(` 		~~~~~~~~~~~~ - request - (error) ~~~ `);
            if (error.response) {
                console.log(` 		~ error.response => `, error.response);
            }
            console.log(` 		~ localStorage.getItem(${_accesstoken_name}) => `, localStorage.getItem(_accesstoken_name));
            console.log(` ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ `);
        }
		
		// return Promise.reject(error);
		if (error.response) {
			if (shouldLogRequestDiagnostics(process.env.VUE_APP_BUILD_ENV, error?.config)) {
                console.log(` 		~ error.response => `, error.response);
                console.log(` 		~ error.response.status => `, error.response.status);
            }

			if (error.response.status === 401) {
				commonLogout()
                return Promise.reject(error);
			} else {
                return Promise.reject(error);
            }
		} else {
			return Promise.reject(error);
		}
	}
);

// Add a response interceptor
_custom_base_axios.interceptors.response.use(
	function (response) {
		// // Do something with response data
		// console.log(` ~~~~~~~~~~~~ - axios interceptors - (response) - (test & develop log) ~~~ `);
		// console.log(` ~~~~~~~~~~~~ - response - (response) ~~~ `);
		// console.log(` ~ response => `, response);
		// console.log(` ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ `);
		return response;
	},
	async function (error) {
		// Do something with response error
		if (shouldLogRequestDiagnostics(process.env.VUE_APP_BUILD_ENV, error?.config)) {
			console.log(` ~~~~~~~~~~~~ - axios response - (test & develop log) ~~~ `);
			console.log(` 		~~~~~~~~~~~~ - response - (error) ~~~ `);
			console.log(` 		~ error => `, error);
			console.log(` ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ `);
		}

		if (error.response) {
			if (shouldLogRequestDiagnostics(process.env.VUE_APP_BUILD_ENV, error?.config)) {
                console.log(` 		~ error.response => `, error.response);
                console.log(` 		~ error.response.status => `, error.response.status);
            }

			if (error.response.status === 401) {
				// JWT 점진 채택: access(JWT) 만료(401) 시 refresh 쿠키로 새 access 를 재발급받아
				// 원 요청을 재시도한다. 공유 헬퍼(refreshAccessToken)가 동시 401 을 dedup 한다
				// (thundering herd 방지). refresh/login 자체·이미 재시도한 요청은 아래 로그아웃 폴백.
				const _originalRequest = error.config || {};
				// 로그아웃 요청 자체의 401 은 logout 액션이 finally 로 정리한다 — refresh·공통 로그아웃을 다시 타면 재귀한다
				if (String(_originalRequest.url || '').endsWith('/logout')) {
					return Promise.reject(error);
				}
				if (isRefreshableAuthError(_originalRequest)) {
					_originalRequest._retriedWithRefresh = true;
					try {
						const _newToken = await refreshAccessToken();
						_originalRequest.headers = _originalRequest.headers || {};
						_originalRequest.headers['Authorization'] = `Bearer ${_newToken}`;
						return _custom_base_axios(_originalRequest);
					} catch (_refreshError) {
						// refresh 실패 → 아래 로그아웃 폴백
						if (shouldLogRequestDiagnostics(process.env.VUE_APP_BUILD_ENV, error?.config)) {
							console.log(` ~ JWT refresh 실패 → logout 폴백 => `, _refreshError);
						}
					}
				}

				// public(비인증) 승인 엔드포인트의 401 은 로그인 세션 문제가 아니라 승인 토큰
				// 자체의 무효/만료이므로, 글로벌 로그아웃/홈 리다이렉트를 건너뛰고 원 에러를
				// 컴포넌트로 전파한다(register-auth 화면 안내문이 표시되도록).
				if (!isPublicAuthExemptEndpoint(_originalRequest)) {
					commonLogout()
				}
                return Promise.reject(error);
			} else {
                return Promise.reject(error);
            }
		} else {
			return Promise.reject(error);
		}
	}
);


export default _custom_base_axios;
